import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import DiscountPolicySection from "../components/DiscountPolicySection";
import { Buildings, FloppyDisk, CheckCircle, Lock, UsersThree, Plus, Trash, Lightning } from "@phosphor-icons/react";

const FIELDS = [
  { key: "name", label: "Company Name", placeholder: "SND DIGITAL PVT LTD", colSpan: 2, required: true },
  { key: "gstin", label: "GSTIN", placeholder: "09ABKCS3947G1ZW", colSpan: 1, mono: true, required: true },
  { key: "state_code", label: "State Code", placeholder: "09", colSpan: 1, mono: true, required: true },
  { key: "state", label: "State", placeholder: "Uttar Pradesh", colSpan: 1, required: true },
  { key: "phone", label: "Phone", placeholder: "9XXXXXXXXX", colSpan: 1, mono: true },
  { key: "email", label: "Email", placeholder: "info@…", colSpan: 1 },
  { key: "city", label: "City (for e-Invoice)", placeholder: "Ghaziabad", colSpan: 1 },
  { key: "pin", label: "PIN Code (for e-Invoice)", placeholder: "201001", colSpan: 1, mono: true },
  { key: "address", label: "Registered Address", placeholder: "Door no, street, city - PIN", colSpan: 2, textarea: true, required: true },
];

export default function CompanySettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [form, setForm] = useState({});
  const [orig, setOrig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setForm(r.data); setOrig(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const change = (k, v) => {
    setForm({ ...form, [k]: v });
    setSaved(false);
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(orig);

  const save = async () => {
    if (!isAdmin) return;
    if (!form.name || !form.gstin || !form.state || !form.state_code || !form.address) {
      alert("Company Name, GSTIN, State, State Code, and Address are required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/settings/company", form);
      setForm(r.data); setOrig(r.data); setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Shell>
      <PageHeader
        title="Company Settings"
        subtitle="Seller details printed on every Delivery Challan and Tax Invoice."
        testId="company-settings-header"
        actions={
          <button
            onClick={save}
            disabled={!isAdmin || !dirty || saving || loading}
            data-testid="save-company-btn"
            className="inline-flex items-center gap-1.5 h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-40"
          >
            {saved ? <><CheckCircle size={14} weight="bold" /> Saved</> : <><FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Changes"}</>}
          </button>
        }
      />
      <div className="p-8 max-w-3xl">
        {!isAdmin && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 inline-flex items-start gap-2">
            <Lock size={14} weight="fill" className="mt-0.5 shrink-0" />
            <div>Only a Super Admin can modify Company Settings. You can view the current values below.</div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="company-form">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200 pb-3">
            <Buildings size={13} weight="bold" /> Seller / Warehouse Identity
          </div>

          {loading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : "col-span-1"}>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </label>
                  {f.textarea ? (
                    <textarea
                      value={form[f.key] || ""}
                      onChange={(e) => change(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={2}
                      disabled={!isAdmin}
                      data-testid={`company-${f.key}`}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50"
                    />
                  ) : (
                    <input
                      value={form[f.key] || ""}
                      onChange={(e) => change(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      disabled={!isAdmin}
                      data-testid={`company-${f.key}`}
                      className={`w-full h-10 px-3 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50 ${f.mono ? "font-mono-num" : ""}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 text-[11px] text-slate-500">
          These details are <span className="font-semibold">snapshotted onto each Delivery Challan / Invoice at creation</span> — past documents will keep
          their original company details even if you change them here.
        </div>

        <DiscountPolicySection isAdmin={isAdmin} />
        <ExecutivesSection isAdmin={isAdmin} />
        <EInvoiceSection isAdmin={isAdmin} />
      </div>
    </Shell>
  );
}

function EInvoiceSection({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/einvoice/settings").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const save = async (patch) => {
    setSaving(true); setMsg("");
    try {
      const r = await api.patch("/einvoice/settings", patch);
      setCfg(r.data); setToken("");
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setMsg("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!isAdmin) return null;
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md p-6" data-testid="einvoice-section">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200 pb-3 mb-4">
        <Lightning size={13} weight="bold" /> E-Invoicing &amp; E-Way Bill (GSTZen)
      </div>
      <div className="text-xs text-slate-500 mb-4">
        Powers IRN + signed QR generation for B2B invoices and E-Way Bills for invoices above ₹50,000.
        Token from <span className="font-mono-num">my.gstzen.in</span> → profile page.
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
            GSTZen API Token {cfg?.configured && <span className="text-emerald-600 normal-case font-normal">— configured ({cfg.token_masked})</span>}
          </label>
          <input value={token} onChange={(e) => setToken(e.target.value)} placeholder={cfg?.configured ? "Paste a new token to replace" : "e.g. 5b2fe14d-…"} data-testid="gstzen-token-input" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
        </div>
        <button onClick={() => save({ token })} disabled={saving || !token.trim()} data-testid="gstzen-token-save" className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-md disabled:opacity-50">Save Token</button>
      </div>
      <label className="mt-4 inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!cfg?.sandbox} onChange={(e) => save({ sandbox: e.target.checked })} data-testid="gstzen-sandbox-toggle" className="w-4 h-4" />
        <span className="text-xs font-semibold text-slate-700">Sandbox mode (test against GSTZen demo — nothing is filed with the government)</span>
      </label>
      {cfg?.sandbox && <div className="mt-1 text-[11px] text-amber-700 font-semibold" data-testid="sandbox-warning">⚠ Sandbox is ON — IRNs/EWBs generated now are NOT real. Turn off before live billing.</div>}
      {msg && <div className="mt-2 text-xs font-semibold text-slate-600" data-testid="einvoice-settings-msg">{msg}</div>}
    </div>
  );
}

function ExecutivesSection({ isAdmin }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/executives").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true); setError("");
    try {
      await api.post("/executives", { name: name.trim() });
      setName("");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setAdding(false); }
  };

  const remove = async (ex) => {
    if (!window.confirm(`Remove executive "${ex.name}"? Past invoices keep their recorded name.`)) return;
    try {
      await api.delete(`/executives/${ex.id}`);
      load();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md p-6" data-testid="executives-section">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200 pb-3 mb-4">
        <UsersThree size={13} weight="bold" /> Sales Executives
      </div>
      <div className="text-xs text-slate-500 mb-4">
        Selecting an executive is <span className="font-semibold">mandatory on every POS bill</span>. This is internal only —
        it is never printed on invoices or shown to customers.
      </div>
      {isAdmin && (
        <div className="flex gap-2 mb-4">
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Executive name e.g. Rahul"
            data-testid="exec-name-input"
            className="flex-1 h-10 px-3 border border-slate-300 rounded-md text-sm"
          />
          <button onClick={add} disabled={adding || !name.trim()} data-testid="exec-add-btn" className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plus size={13} weight="bold" /> Add
          </button>
        </div>
      )}
      {error && <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2" data-testid="exec-error">{error}</div>}
      {list.length === 0 ? (
        <div className="text-sm text-slate-400 py-4 text-center" data-testid="exec-empty">No executives yet{isAdmin ? " — add the first one above." : "."}</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="exec-list">
          {list.map((ex) => (
            <div key={ex.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`exec-row-${ex.name}`}>
              <div className="text-sm font-medium">{ex.name}</div>
              {isAdmin && (
                <button onClick={() => remove(ex)} data-testid={`exec-del-${ex.name}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md" title="Remove">
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
